<template>
  <component
    :is="tag"
    :class="classes"
    :style="styles"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template v-if="title">
      {{ title }}
    </template>

    <slot v-else />
  </component>
</template>

<script>
  export default {
    name: 'BaseHeading',

    inject: {
      theme: {
        default: () => ({ isDark: false }),
      },
      heading: {
        default: () => ({ align: 'left' }),
      },
    },

    provide () {
      return {
        heading: {
          align: this.align,
        },
      }
    },

    props: {
      align: {
        type: String,
        default () {
          return this.heading.align
        },
      },
      dense: {
        type: Boolean,
        default () {
          return this.isDense
        },
      },
      size: {
        type: String,
        default: 'text-h3',
      },
      space: {
        type: [Number, String],
        default: 4,
      },
      mobileSize: {
        type: String,
        default: 'text-h4',
      },
      mobileBreakpoint: {
        type: [Number, String],
        default: 768,
      },
      tag: {
        type: String,
        default: 'h1',
      },
      title: String,
      weight: {
        type: String,
        default: 'black',
      },
      color: {
        type: String,
        default: null,
      },
    },

    computed: {
      classes () {
        const classes = [
          this.fontSize,
          `font-weight-${this.weight}`,
          `mb-${this.space}`,
          `text-${this.align}`,
        ]

        if (this.fontSizeValue) classes.push('has-custom-size')

        // 如果传入了颜色，则不添加默认的 white--text
        if (!this.color) {
          classes.push(this.theme.isDark && 'white--text')
        }

        return classes
      },
      // 当前断点下的原始 size 值（Vuetify 类名 或 CSS 值）
      currentSize () {
        return this.$vuetify.breakpoint.width >= this.mobileBreakpoint
          ? this.size
          : this.mobileSize
      },
      // 如果是 Vuetify 类名（text- 开头）则返回类名；
      // 若为自定义 CSS 值，则返回该断点 prop 的默认类名，保留行高、字重等排版样式
      fontSize () {
        const val = this.currentSize
        if (!val || val.startsWith('text-')) return val
        // 自定义 CSS 值：取对应断点 prop 的默认类名
        const isDesktop = this.$vuetify.breakpoint.width >= this.mobileBreakpoint
        const propKey = isDesktop ? 'size' : 'mobileSize'
        const propDef = this.$options.props && this.$options.props[propKey]
        const def = propDef
          ? (typeof propDef.default === 'function' ? propDef.default.call(this) : propDef.default)
          : ''
        return (typeof def === 'string' && def.startsWith('text-')) ? def : ''
      },
      // 如果是自由 CSS 值（如 2rem, 32px, 1.5vw）则返回该值用于内联样式
      fontSizeValue () {
        const val = this.currentSize
        return (val && !val.startsWith('text-')) ? val : null
      },
      styles () {
        const result = this.color ? { color: this.color } : {}
        // 使用 CSS 自定义属性传递字号，由 scoped 样式的 !important 覆盖 Vuetify 类的 font-size !important
        if (this.fontSizeValue) result['--heading-custom-size'] = this.fontSizeValue
        return result
      },

    },
  }
</script>

<style scoped>
/* 当传入自定义 CSS 字号值时，用更高优先级覆盖 Vuetify text-* 类的 font-size !important */
.has-custom-size {
  font-size: var(--heading-custom-size) !important;
}
</style>
